import React from "react";
import CcButton from "../../CoilConfigurator/components/CcButton";
import InputWithUnitSelect from "../../CoilConfigurator/components/InputWithUnitSelect";
import Stepper, { type StepItem } from "../components/Stepper";
import type {
  GeometryUnit,
  PipeSpecification,
  ProcessSpecification,
  ProjectInformation,
  ProjectSpecification,
  ThicknessUnit,
} from "../models/types";

export type WizardStep = "project" | "pipe" | "process" | "review";

const STEPS: StepItem[] = [
  { id: "project", label: "Project" },
  { id: "pipe", label: "Pipe" },
  { id: "process", label: "Process" },
  { id: "review", label: "Review" },
];

const GEOMETRY_UNITS: Array<{ value: GeometryUnit; label: string }> = [
  { value: "mm", label: "mm" },
  { value: "in", label: "in" },
];

const THICKNESS_UNITS: Array<{ value: ThicknessUnit; label: string }> = [
  { value: "mm", label: "mm" },
  { value: "in", label: "in" },
  { value: "mil", label: "mil" },
];

interface NewConfigurationWizardProps {
  step: WizardStep;
  spec: ProjectSpecification;
  errors: Record<string, string>;
  onChangeProject: (patch: Partial<ProjectInformation>) => void;
  onChangePipe: (patch: Partial<PipeSpecification>) => void;
  onChangeProcess: (patch: Partial<ProcessSpecification>) => void;
  onBack: () => void;
  onContinue: () => void;
  onCancel: () => void;
  onGenerate: () => void;
}

const Field = ({
  id,
  label,
  required,
  error,
  children,
}: {
  id: string;
  label: string;
  required?: boolean;
  error?: string;
  children: React.ReactNode;
}) => (
  <div className="modal-field">
    <label htmlFor={id}>
      {label}
      {required ? <span className="op-required"> *</span> : null}
    </label>
    {children}
    {error ? (
      <p className="op-field-error" role="alert">
        {error}
      </p>
    ) : null}
  </div>
);

const NewConfigurationWizard = ({
  step,
  spec,
  errors,
  onChangeProject,
  onChangePipe,
  onChangeProcess,
  onBack,
  onContinue,
  onCancel,
  onGenerate,
}: NewConfigurationWizardProps) => {
  const completed = STEPS.filter((_, index) => index < STEPS.findIndex((s) => s.id === step)).map(
    (s) => s.id,
  );

  return (
    <div className="op-wizard">
      <Stepper steps={STEPS} current={step} completed={completed} />

      {step === "project" && (
        <section className="op-form-card">
          <h2 className="op-section-title">Project Information</h2>
          <div className="op-form-grid">
            <Field id="projectNumber" label="Project Number" required error={errors.projectNumber}>
              <input
                id="projectNumber"
                className="op-input"
                placeholder="Enter project number"
                value={spec.project.projectNumber}
                onChange={(e) => onChangeProject({ projectNumber: e.target.value })}
              />
            </Field>
            <Field id="projectName" label="Project Name" required error={errors.projectName}>
              <input
                id="projectName"
                className="op-input"
                placeholder="Enter project name"
                value={spec.project.projectName}
                onChange={(e) => onChangeProject({ projectName: e.target.value })}
              />
            </Field>
            <Field id="customer" label="Customer" required error={errors.customer}>
              <input
                id="customer"
                className="op-input"
                placeholder="Enter customer"
                value={spec.project.customer}
                onChange={(e) => onChangeProject({ customer: e.target.value })}
              />
            </Field>
            <Field id="engineer" label="Engineer" required error={errors.engineer}>
              <input
                id="engineer"
                className="op-input"
                placeholder="Enter engineer name"
                value={spec.project.engineer}
                onChange={(e) => onChangeProject({ engineer: e.target.value })}
              />
            </Field>
            <Field id="projectRevision" label="Project Revision">
              <input
                id="projectRevision"
                className="op-input"
                value={spec.project.projectRevision}
                onChange={(e) => onChangeProject({ projectRevision: e.target.value })}
              />
            </Field>
            <Field id="configurationDate" label="Configuration Date">
              <input
                id="configurationDate"
                type="date"
                className="op-input"
                value={spec.project.configurationDate}
                onChange={(e) => onChangeProject({ configurationDate: e.target.value })}
              />
            </Field>
          </div>
        </section>
      )}

      {step === "pipe" && (
        <section className="op-form-card">
          <h2 className="op-section-title">Pipe Specification</h2>
          <div className="op-form-grid">
            <Field id="outsideDiameter" label="Pipe Outside Diameter" required error={errors.outsideDiameter}>
              <InputWithUnitSelect
                id="outsideDiameter"
                name="outsideDiameter"
                value={spec.pipe.outsideDiameter}
                unit={spec.pipe.outsideDiameterUnit}
                unitOptions={GEOMETRY_UNITS}
                onValueChange={(value) => onChangePipe({ outsideDiameter: value })}
                onUnitChange={(unit) => onChangePipe({ outsideDiameterUnit: unit })}
              />
            </Field>
            <Field id="wallThickness" label="Pipe Wall Thickness" required error={errors.wallThickness}>
              <InputWithUnitSelect
                id="wallThickness"
                name="wallThickness"
                value={spec.pipe.wallThickness}
                unit={spec.pipe.wallThicknessUnit}
                unitOptions={THICKNESS_UNITS}
                onValueChange={(value) => onChangePipe({ wallThickness: value })}
                onUnitChange={(unit) => onChangePipe({ wallThicknessUnit: unit })}
              />
            </Field>
            <Field id="pipeGrade" label="Pipe Grade" required error={errors.pipeGrade}>
              <input
                id="pipeGrade"
                className="op-input"
                placeholder="For example X65"
                value={spec.pipe.pipeGrade}
                onChange={(e) => onChangePipe({ pipeGrade: e.target.value })}
              />
            </Field>
            <Field id="pipeLength" label="Pipe Length">
              <InputWithUnitSelect
                id="pipeLength"
                name="pipeLength"
                value={spec.pipe.pipeLength}
                unit={spec.pipe.pipeLengthUnit}
                unitOptions={GEOMETRY_UNITS}
                onValueChange={(value) => onChangePipe({ pipeLength: value })}
                onUnitChange={(unit) => onChangePipe({ pipeLengthUnit: unit })}
              />
            </Field>
            <Field id="pipeTolerance" label="Pipe Tolerance">
              <input
                id="pipeTolerance"
                className="op-input"
                placeholder="Optional tolerance"
                value={spec.pipe.pipeTolerance}
                onChange={(e) => onChangePipe({ pipeTolerance: e.target.value })}
              />
            </Field>
            <Field id="pipeGeometry" label="Pipe Geometry">
              <select
                id="pipeGeometry"
                className="op-input"
                value={spec.pipe.pipeGeometry}
                onChange={(e) => onChangePipe({ pipeGeometry: e.target.value })}
              >
                <option value="Straight">Straight</option>
                <option value="Elbow">Elbow</option>
                <option value="Bend">Bend</option>
                <option value="Other">Other</option>
              </select>
            </Field>
          </div>

          <h3 className="op-subsection-title">Field joint cutbacks</h3>
          <p className="op-muted op-cutback-note">
            Supply both long and short cutbacks to close length versions without CAD confirmation.
          </p>
          <div className="op-form-grid">
            <Field
              id="cutbackLongSide"
              label="Cutback — long side"
              error={errors.cutbackLongSide}
            >
              <InputWithUnitSelect
                id="cutbackLongSide"
                name="cutbackLongSide"
                value={spec.pipe.cutbackLongSide}
                unit={spec.pipe.cutbackLongSideUnit}
                unitOptions={GEOMETRY_UNITS}
                onValueChange={(value) => onChangePipe({ cutbackLongSide: value })}
                onUnitChange={(unit) => onChangePipe({ cutbackLongSideUnit: unit })}
              />
            </Field>
            <Field
              id="cutbackShortSide"
              label="Cutback — short side"
              error={errors.cutbackShortSide}
            >
              <InputWithUnitSelect
                id="cutbackShortSide"
                name="cutbackShortSide"
                value={spec.pipe.cutbackShortSide}
                unit={spec.pipe.cutbackShortSideUnit}
                unitOptions={GEOMETRY_UNITS}
                onValueChange={(value) => onChangePipe({ cutbackShortSide: value })}
                onUnitChange={(unit) => onChangePipe({ cutbackShortSideUnit: unit })}
              />
            </Field>
            <Field id="chamferAngleDeg" label="Chamfer angle" error={errors.chamferAngleDeg}>
              <input
                id="chamferAngleDeg"
                className="op-input"
                type="number"
                min="0"
                step="0.1"
                placeholder="Degrees"
                value={spec.pipe.chamferAngleDeg}
                onChange={(e) => onChangePipe({ chamferAngleDeg: e.target.value })}
              />
            </Field>
          </div>
        </section>
      )}

      {step === "process" && (
        <section className="op-form-card">
          <h2 className="op-section-title">Process / Coating Specification</h2>
          <div className="op-form-grid">
            <Field id="coatingType" label="Coating Type">
              <input
                id="coatingType"
                className="op-input"
                placeholder="Enter coating type"
                value={spec.process.coatingType}
                onChange={(e) => onChangeProcess({ coatingType: e.target.value })}
              />
            </Field>
            <Field id="coatingThickness" label="Coating Thickness">
              <InputWithUnitSelect
                id="coatingThickness"
                name="coatingThickness"
                value={spec.process.coatingThickness}
                unit={spec.process.coatingThicknessUnit}
                unitOptions={THICKNESS_UNITS}
                onValueChange={(value) => onChangeProcess({ coatingThickness: value })}
                onUnitChange={(unit) => onChangeProcess({ coatingThicknessUnit: unit })}
              />
            </Field>
            <Field id="heatingRequirement" label="Heating Requirement">
              <input
                id="heatingRequirement"
                className="op-input"
                placeholder="Optional heating requirement"
                value={spec.process.heatingRequirement}
                onChange={(e) => onChangeProcess({ heatingRequirement: e.target.value })}
              />
            </Field>
            <Field id="blastRequirement" label="Blast Requirement">
              <input
                id="blastRequirement"
                className="op-input"
                placeholder="Optional blast requirement"
                value={spec.process.blastRequirement}
                onChange={(e) => onChangeProcess({ blastRequirement: e.target.value })}
              />
            </Field>
            <Field id="surfacePreparation" label="Surface Preparation Requirement">
              <input
                id="surfacePreparation"
                className="op-input"
                placeholder="Optional surface prep requirement"
                value={spec.process.surfacePreparation}
                onChange={(e) => onChangeProcess({ surfacePreparation: e.target.value })}
              />
            </Field>
          </div>
        </section>
      )}

      {step === "review" && (
        <section className="op-form-card">
          <h2 className="op-section-title">Review Project</h2>
          <div className="op-review-grid">
            <div>
              <h3>Project</h3>
              <p>Project Number: {spec.project.projectNumber}</p>
              <p>Project Name: {spec.project.projectName}</p>
              <p>Customer: {spec.project.customer}</p>
              <p>Engineer: {spec.project.engineer}</p>
            </div>
            <div>
              <h3>Pipe</h3>
              <p>
                Outside Diameter: {spec.pipe.outsideDiameter} {spec.pipe.outsideDiameterUnit}
              </p>
              <p>
                Wall Thickness: {spec.pipe.wallThickness} {spec.pipe.wallThicknessUnit}
              </p>
              <p>Grade: {spec.pipe.pipeGrade}</p>
              {spec.pipe.cutbackLongSide || spec.pipe.cutbackShortSide ? (
                <>
                  <p>
                    Cutback long: {spec.pipe.cutbackLongSide || "—"} {spec.pipe.cutbackLongSideUnit}
                  </p>
                  <p>
                    Cutback short: {spec.pipe.cutbackShortSide || "—"}{" "}
                    {spec.pipe.cutbackShortSideUnit}
                  </p>
                  {spec.pipe.chamferAngleDeg ? (
                    <p>Chamfer: {spec.pipe.chamferAngleDeg}°</p>
                  ) : null}
                </>
              ) : null}
            </div>
            <div>
              <h3>Coating</h3>
              <p>Type: {spec.process.coatingType || "—"}</p>
              <p>
                Thickness:{" "}
                {spec.process.coatingThickness
                  ? `${spec.process.coatingThickness} ${spec.process.coatingThicknessUnit}`
                  : "—"}
              </p>
            </div>
          </div>
        </section>
      )}

      <div className="op-wizard-actions">
        <CcButton variant="secondary" onClick={step === "project" ? onCancel : onBack}>
          {step === "project" ? "Cancel" : "Back"}
        </CcButton>
        {step === "review" ? (
          <CcButton variant="primary" onClick={onGenerate}>
            Generate Configuration
          </CcButton>
        ) : (
          <CcButton variant="primary" onClick={onContinue}>
            Continue
          </CcButton>
        )}
      </div>
    </div>
  );
};

export default NewConfigurationWizard;
